import { useMemo } from 'react';

import {
  SUGGESTED_ANSWER_EXAMPLE,
  SUGGESTED_ANSWER_FIXED,
  SUGGESTED_ANSWER_VARY,
} from 'global/constants';
import { isRichtextEmpty } from 'approot/shared/richtext/richtext.utils';
import { PStyled } from 'approot/shared/styles/typography.style';
import { Box } from 'approot/shared/design-system/box/box.style';
import { ComponentDraw } from '../../component.constants';
import { SettingsGroup } from 'approot/activity-builder/settings/settings.style';
import { ImageStatic } from 'approot/shared/images/images';
import { ZoomComponentTrigger } from 'approot/activity-builder/activity-builder-zoom-component-modal';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import { AnswerDrawViewImage } from './suggested-answer-draw-view.style';
import SuggestedAnswerTitle from '../suggested-answer-title';
import SuggestedAnswerDrawViewImageModal from './suggested-answer-draw-view-modal';
import {
  useGetImageAnswer,
  useGetRichtextAnswer,
} from '../suggested-answer.hooks';
import SuggestedAnswerExampleRichtext from './suggested-answer-example-richtext';
import SuggestedAnswerViewBadges from './suggested-answer-view-badges';

type Props = {
  component: ComponentDraw;
  isUnitQuiz: boolean;
};

const SuggestedAnswerDrawView = ({ component, isUnitQuiz }: Props) => {
  const { id, suggestedAnswerProperties } = component;
  const answerType = suggestedAnswerProperties?.suggestedAnswerType;

  const { isVisible, openModal, closeModal } = useSimpleModalState();

  const richtextAnswer = useGetRichtextAnswer({
    componentId: id,
    suggestedAnswerProperties,
  });

  const imageAnswer = useGetImageAnswer({
    componentId: id,
    suggestedAnswerProperties,
  });

  const hasAnswerContent = useMemo(() => {
    return imageAnswer || !isRichtextEmpty(richtextAnswer);
  }, [imageAnswer, richtextAnswer]);

  return answerType === SUGGESTED_ANSWER_VARY ||
    hasAnswerContent ||
    isUnitQuiz ? (
    <Box>
      <SuggestedAnswerTitle
        isSmall
        label={component.subActivityProperties?.subActivityLabel}
        instructions={component.subActivityProperties?.instructions}
      />

      <SuggestedAnswerViewBadges
        isExampleAnswer={
          hasAnswerContent && answerType === SUGGESTED_ANSWER_EXAMPLE
        }
        isFixedAnswer={
          hasAnswerContent && answerType === SUGGESTED_ANSWER_FIXED
        }
        suggestedAnswerScore={suggestedAnswerProperties?.suggestedAnswersScore}
        isUnitQuiz={isUnitQuiz}
      />

      {answerType === SUGGESTED_ANSWER_VARY ? (
        <PStyled>Answers may vary</PStyled>
      ) : (
        <>
          {isVisible && imageAnswer.original && (
            <SuggestedAnswerDrawViewImageModal
              imageUrl={imageAnswer.original}
              alt={imageAnswer.alt}
              onClose={closeModal}
            />
          )}

          {imageAnswer && imageAnswer.original && (
            <SettingsGroup>
              <AnswerDrawViewImage>
                <ImageStatic
                  imageUrl={imageAnswer.original}
                  alt={imageAnswer.alt || ''}
                />
                <ZoomComponentTrigger
                  ariaLabel="Open draw image answer in a modal"
                  openModal={openModal}
                />
              </AnswerDrawViewImage>
            </SettingsGroup>
          )}

          <SuggestedAnswerExampleRichtext
            id={id}
            suggestedAnswerProperties={suggestedAnswerProperties}
          />
        </>
      )}
    </Box>
  ) : null;
};

export default SuggestedAnswerDrawView;
